import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  getKioskDevices, registerKioskDevice, deleteKioskDevice,
  getSekolahList 
} from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { 
  ChevronLeft, Smartphone, Plus, Trash2, Loader2, 
  RefreshCw, Copy, Check, QrCode, ExternalLink, Building2, Clock
} from 'lucide-react';
import { toast } from '../ui/sonner';

const KioskManagementPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [devices, setDevices] = useState([]);
  const [sekolahList, setSekolahList] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Register dialog
  const [showRegisterDialog, setShowRegisterDialog] = useState(false);
  const [newDeviceName, setNewDeviceName] = useState('');
  const [newDeviceSekolah, setNewDeviceSekolah] = useState('');
  const [registering, setRegistering] = useState(false);
  
  // Token display dialog
  const [showTokenDialog, setShowTokenDialog] = useState(false);
  const [newDevice, setNewDevice] = useState(null);
  const [copied, setCopied] = useState(false);

  const canAccess = ['admin_dinas', 'kepala_sekolah'].includes(user?.role);
  const isKepalaSekolah = user?.role === 'kepala_sekolah';

  useEffect(() => {
    if (canAccess) {
      loadData();
    }
  }, [canAccess]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      const devicesData = await getKioskDevices();
      setDevices(devicesData);
      
      if (!isKepalaSekolah) {
        const schools = await getSekolahList();
        setSekolahList(schools);
      }
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Gagal memuat data');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async () => {
    if (!newDeviceName.trim()) {
      toast.error('Nama device harus diisi');
      return;
    }
    
    const sekolahId = isKepalaSekolah ? user.sekolah_id : newDeviceSekolah;
    if (!sekolahId) {
      toast.error('Pilih sekolah terlebih dahulu');
      return;
    }

    setRegistering(true);
    try {
      const result = await registerKioskDevice({
        device_name: newDeviceName,
        sekolah_id: sekolahId
      });
      
      setNewDevice(result);
      setShowRegisterDialog(false);
      setShowTokenDialog(true);
      setNewDeviceName('');
      setNewDeviceSekolah('');
      
      toast.success('Device berhasil didaftarkan!');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal mendaftarkan device');
    } finally {
      setRegistering(false);
    }
  };

  const handleDelete = async (device) => {
    if (!window.confirm(`Hapus device "${device.device_name}"? Device tidak akan bisa digunakan untuk absensi.`)) {
      return;
    }

    try {
      await deleteKioskDevice(device.id);
      toast.success('Device berhasil dihapus');
      loadData();
    } catch (error) {
      toast.error('Gagal menghapus device');
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success('Token berhasil disalin');
    } catch (error) {
      toast.error('Gagal menyalin token');
    }
  };

  const getKioskUrl = (token) => {
    return `${window.location.origin}/kiosk/${token}`;
  };

  if (!canAccess) {
    return (
      <div className="p-4 md:p-6">
        <Alert className="border-red-200 bg-red-50">
          <AlertDescription className="text-red-800">
            Anda tidak memiliki akses ke halaman ini.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="kiosk-management-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-indigo-950">Mode Kiosk</h1>
            <p className="text-gray-500">Kelola device station absensi</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadData} className="border-indigo-200">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button onClick={() => setShowRegisterDialog(true)} className="bg-emerald-600 hover:bg-emerald-700">
            <Plus className="w-4 h-4 mr-2" />
            Daftarkan Device
          </Button>
        </div>
      </div>

      {/* Info Card */}
      <Alert className="mb-6 border-indigo-200 bg-indigo-50">
        <Smartphone className="w-4 h-4 text-indigo-600" />
        <AlertDescription className="text-indigo-800">
          <strong>Mode Kiosk</strong> memungkinkan satu device digunakan untuk absensi banyak guru. 
          Guru cukup pilih nama dan verifikasi wajah - tanpa perlu login.
        </AlertDescription>
      </Alert>

      {/* Device List */}
      <Card className="border-indigo-100/50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Smartphone className="w-5 h-5 text-indigo-600" />
            Device Terdaftar ({devices.length})
          </CardTitle>
          <CardDescription>
            Daftar device yang sudah dikonfigurasi sebagai station absensi
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
            </div>
          ) : devices.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Smartphone className="w-12 h-12 mx-auto mb-2 text-gray-300" />
              <p>Belum ada device yang didaftarkan</p>
              <Button 
                onClick={() => setShowRegisterDialog(true)} 
                variant="outline" 
                className="mt-4"
              >
                <Plus className="w-4 h-4 mr-2" />
                Daftarkan Device Pertama
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {devices.map((device) => (
                <div 
                  key={device.id}
                  className="flex items-center justify-between p-4 rounded-lg border bg-white hover:bg-gray-50"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center">
                      <Smartphone className="w-6 h-6 text-indigo-600" />
                    </div>
                    <div>
                      <p className="font-medium text-indigo-950">{device.device_name}</p>
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <Building2 className="w-3 h-3" />
                        {device.sekolah_nama}
                      </div>
                      {device.last_used && (
                        <div className="flex items-center gap-1 text-xs text-gray-400 mt-1">
                          <Clock className="w-3 h-3" />
                          Terakhir digunakan: {new Date(device.last_used).toLocaleString('id-ID')}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Badge className={device.is_active ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-600"}>
                      {device.is_active ? 'Aktif' : 'Nonaktif'}
                    </Badge>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setNewDevice(device);
                        setShowTokenDialog(true);
                      }}
                      className="border-indigo-200"
                    >
                      <QrCode className="w-4 h-4 mr-1" />
                      Link
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(device)}
                      className="border-red-200 text-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Register Device Dialog */}
      <Dialog open={showRegisterDialog} onOpenChange={setShowRegisterDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Daftarkan Device Kiosk</DialogTitle>
            <DialogDescription>
              Daftarkan device baru sebagai station absensi untuk sekolah
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nama Device</Label>
              <Input
                placeholder="Contoh: Tablet Ruang Guru"
                value={newDeviceName}
                onChange={(e) => setNewDeviceName(e.target.value)}
              />
            </div>
            
            {!isKepalaSekolah && (
              <div className="space-y-2">
                <Label>Sekolah</Label>
                <Select value={newDeviceSekolah} onValueChange={setNewDeviceSekolah}>
                  <SelectTrigger>
                    <SelectValue placeholder="Pilih sekolah" />
                  </SelectTrigger>
                  <SelectContent>
                    {sekolahList.map(s => (
                      <SelectItem key={s.id} value={s.id}>{s.nama}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRegisterDialog(false)}>
              Batal
            </Button>
            <Button onClick={handleRegister} disabled={registering} className="bg-emerald-600 hover:bg-emerald-700">
              {registering ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Daftarkan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Token Display Dialog */}
      <Dialog open={showTokenDialog} onOpenChange={setShowTokenDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <QrCode className="w-5 h-5 text-indigo-600" />
              Link Kiosk
            </DialogTitle>
            <DialogDescription>
              Gunakan link berikut untuk membuka mode kiosk di device
            </DialogDescription>
          </DialogHeader>
          
          {newDevice && (
            <div className="space-y-4 py-4">
              <div className="p-4 bg-indigo-50 rounded-lg border border-indigo-200">
                <p className="text-sm font-medium text-indigo-800 mb-2">Device: {newDevice.device_name}</p>
                <p className="text-sm text-indigo-600">Sekolah: {newDevice.sekolah_nama}</p>
              </div>
              
              <div className="space-y-2">
                <Label>Link Kiosk</Label>
                <div className="flex gap-2">
                  <Input 
                    value={getKioskUrl(newDevice.device_token)} 
                    readOnly 
                    className="font-mono text-sm"
                  />
                  <Button
                    variant="outline"
                    onClick={() => copyToClipboard(getKioskUrl(newDevice.device_token))}
                  >
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
              
              <Alert className="border-amber-200 bg-amber-50">
                <AlertDescription className="text-amber-800 text-sm">
                  <strong>Penting:</strong> Simpan link ini dengan aman. Buka link ini di device yang akan dijadikan kiosk, 
                  lalu tambahkan ke Home Screen untuk pengalaman seperti aplikasi.
                </AlertDescription>
              </Alert>
              
              <Button 
                onClick={() => window.open(getKioskUrl(newDevice.device_token), '_blank')}
                className="w-full"
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Buka Link di Tab Baru
              </Button>
            </div>
          )}
          
          <DialogFooter>
            <Button onClick={() => setShowTokenDialog(false)}>Tutup</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default KioskManagementPage;
